import React, { useMemo, Suspense, useState } from 'react';

import { ApiErrorDialog } from '@components/dev/ApiErrorDialog';
import { ErrorFallback } from '@components/ErrorFallback';
import { RootAriaLabelMuiBugFix } from '@components/RootAriaLabelMuiBugFix';
import { ZendeskIframe } from '@components/zendesk/ZendeskIframe';
import { ThemeProvider } from '@material-ui/styles';
import { FormOptionsProvider } from '@monoid-dev/mobx-zod-form-react';
import { Loading } from '@pages/Loading';
import { ErrorBoundary, captureException } from '@sentry/react';
import { muiTheme } from '@styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PickerUtilsProvider } from '@utils/PickupUtilsProvider';
import { initTracing, mobxFormMetricsPlugin } from '@utils/tracing';
import { createBrowserHistory } from 'history';
import { Router } from 'react-router-dom';

import SplitPages from './SplitPages';

import './App.css';

const history = createBrowserHistory();

initTracing(history);

function App() {
  const [errors, setErrors] = useState([] as any[]);
  const onError = (e: any) => {
    captureException(e);
    if (
      (process.env.VITE_APP_STAGE !== 'production' || import.meta.env.DEV) &&
      process.env.VITE_APP_STAGE !== 'e2e' &&
      !localStorage.getItem('INGORE_API_ERROR_DIALOG')
    ) {
      setErrors((s) => [...s, e]);
    }
  };

  const client = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          mutations: {
            retry: 0,
            onError,
          },
          queries: {
            retry: 0,
            onError,
          },
        },
      }),
    [],
  );

  useMemo(
    () =>
      console.info(
        `${document.title} ${location.toString()} entered at ${window.performance?.now()}`,
      ),
    [],
  );

  return (
    <>
      <FormOptionsProvider
        options={{
          get plugins() {
            return [mobxFormMetricsPlugin()];
          },
        }}
      >
        <PickerUtilsProvider>
          <QueryClientProvider client={client}>
            <ThemeProvider theme={muiTheme}>
              <Router history={history}>
                <ErrorBoundary
                  beforeCapture={(e) => {
                    e.setLevel('fatal');
                    console.error('Site crashed!');
                  }}
                  fallback={(p) => <ErrorFallback {...p} />}
                >
                  <Suspense fallback={<Loading />}>
                    <SplitPages />
                  </Suspense>
                </ErrorBoundary>

                <ZendeskIframe />
              </Router>

              <ApiErrorDialog errors={errors} onClose={() => setErrors([])} />
            </ThemeProvider>

            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </PickerUtilsProvider>
      </FormOptionsProvider>
      <RootAriaLabelMuiBugFix />
    </>
  );
}

export default App;
